import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../components/ConfirmModal';
import { FiPlus, FiEdit2, FiTrash2, FiArrowLeft, FiChevronDown, FiChevronUp, FiRefreshCw } from 'react-icons/fi';

const INP = { width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:"'DM Sans',sans-serif", background:'#fafbfc' };
const LBL = { display:'block', fontSize:11, fontWeight:700, color:'#475569', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.04em' };

const PLATFORM_LABELS = { upwork_personal:'Upwork Personal', upwork_company:'Upwork Company' };
const PLATFORM_COLORS = { upwork_personal:{ bg:'#e0f2fe', text:'#0369a1' }, upwork_company:{ bg:'#dbeafe', text:'#1e40af' } };
const STATUS_COLORS   = { active:{ bg:'#dcfce7', text:'#15803d' }, paused:{ bg:'#fef9c3', text:'#854d0e' }, ended:{ bg:'#f1f5f9', text:'#475569' }, suspended:{ bg:'#fee2e2', text:'#991b1b' } };
const MS_COLORS       = { pending:{ bg:'#f1f5f9', text:'#475569' }, funded:{ bg:'#e0f2fe', text:'#0369a1' }, in_progress:{ bg:'#dbeafe', text:'#1e40af' }, submitted:{ bg:'#fef9c3', text:'#854d0e' }, approved:{ bg:'#dcfce7', text:'#15803d' }, released:{ bg:'#bbf7d0', text:'#14532d' } };
const CY_COLORS       = { open:{ bg:'#f1f5f9', text:'#475569' }, pending_review:{ bg:'#fef9c3', text:'#854d0e' }, billed:{ bg:'#dbeafe', text:'#1e40af' }, paid:{ bg:'#dcfce7', text:'#15803d' } };
const fmt = (n) => '$' + Number(n||0).toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 });

const badge = (label, style) => (
  <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, ...style }}>{label}</span>
);

const emptyContract = { contractId:'', title:'', type:'hourly', status:'active', platform:'upwork_company', clientName:'', projectId:'', totalAmount:'', hourlyRate:'', weeklyLimit:'', feePercent:10, currency:'USD', startDate:'', endDate:'', notes:'' };
const emptyMS = { name:'', description:'', amount:'', status:'pending', dueDate:'' };
const emptyCY = { weekStart:'', weekEnd:'', hoursWorked:'', feePercent:10 };

export default function Upwork() {
  const confirm = useConfirm();
  const [contracts, setContracts] = useState([]);
  const [stats,     setStats]     = useState(null);
  const [projects,  setProjects]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null);
  const [expanded,  setExpanded]  = useState({});

  // Modals
  const [contractModal, setContractModal] = useState(false);
  const [editContract,  setEditContract]  = useState(null);
  const [contractForm,  setContractForm]  = useState(emptyContract);
  const [savingC,       setSavingC]       = useState(false);

  const [msModal, setMsModal] = useState(false);
  const [editMs,  setEditMs]  = useState(null);
  const [msForm,  setMsForm]  = useState(emptyMS);
  const [savingMs,setSavingMs]= useState(false);

  const [cyModal, setCyModal] = useState(false);
  const [editCy,  setEditCy]  = useState(null);
  const [cyForm,  setCyForm]  = useState(emptyCY);
  const [savingCy,setSavingCy]= useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cr, sr, pr] = await Promise.all([
        api.get('/upwork'),
        api.get('/upwork/stats'),
        api.get('/projects'),
      ]);
      setContracts(cr.data.data || []);
      setStats(sr.data.data);
      setProjects(pr.data.projects || []);
      if (selected) {
        const fresh = (cr.data.data || []).find(c => c.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch(e) { toast.error('Failed to load'); }
    setLoading(false);
  }, [selected]);

  useEffect(() => { loadAll(); }, []);

  // ── Contract CRUD ──────────────────────────────────────────────────────────
  const openAddContract = () => { setEditContract(null); setContractForm(emptyContract); setContractModal(true); };
  const openEditContract = (c, e) => { e.stopPropagation(); setEditContract(c); setContractForm({ ...c, projectId: c.projectId||'' }); setContractModal(true); };

  const saveContract = async (e) => {
    e.preventDefault();
    setSavingC(true);
    try {
      if (editContract) {
        await api.put(`/upwork/${editContract.id}`, contractForm);
        toast.success('Contract updated');
      } else {
        await api.post('/upwork', contractForm);
        toast.success('Contract added');
      }
      setContractModal(false);
      loadAll();
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    setSavingC(false);
  };

  const deleteContract = async (c, e) => {
    e.stopPropagation();
    if (!await confirm({ title:'Delete Contract', message:`Delete "${c.title}"? All milestones and cycles will be removed.`, confirmText:'Delete', danger:true })) return;
    try {
      await api.delete(`/upwork/${c.id}`);
      toast.success('Deleted');
      if (selected?.id === c.id) setSelected(null);
      loadAll();
    } catch { toast.error('Failed to delete'); }
  };

  // ── Milestone CRUD ─────────────────────────────────────────────────────────
  const openAddMs = () => { setEditMs(null); setMsForm(emptyMS); setMsModal(true); };
  const openEditMs = (m) => { setEditMs(m); setMsForm({ name:m.name, description:m.description||'', amount:m.amount, status:m.status, dueDate:m.dueDate||'' }); setMsModal(true); };

  const saveMilestone = async (e) => {
    e.preventDefault();
    setSavingMs(true);
    try {
      if (editMs) {
        await api.put(`/upwork/${selected.id}/milestones/${editMs.id}`, msForm);
        toast.success('Milestone updated');
      } else {
        await api.post(`/upwork/${selected.id}/milestones`, msForm);
        toast.success('Milestone added');
      }
      setMsModal(false);
      loadAll();
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    setSavingMs(false);
  };

  const deleteMs = async (m) => {
    if (!await confirm({ title:'Delete Milestone', message:`Delete "${m.name}"?`, confirmText:'Delete', danger:true })) return;
    try { await api.delete(`/upwork/${selected.id}/milestones/${m.id}`); toast.success('Deleted'); loadAll(); }
    catch { toast.error('Failed'); }
  };

  // ── Cycle CRUD ─────────────────────────────────────────────────────────────
  const openAddCy = () => {
    const now = new Date();
    const day = now.getDay(); // 0=Sun
    const mon = new Date(now); mon.setDate(now.getDate() - ((day + 6) % 7));
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    const fmt2 = d => d.toISOString().slice(0,10);
    setEditCy(null);
    setCyForm({ ...emptyCY, weekStart: fmt2(mon), weekEnd: fmt2(sun), feePercent: selected?.feePercent || 10 });
    setCyModal(true);
  };
  const openEditCy = (cy) => { setEditCy(cy); setCyForm({ weekStart:cy.weekStart, weekEnd:cy.weekEnd, hoursWorked:cy.hoursWorked, feePercent:cy.feePercent, status:cy.status }); setCyModal(true); };

  const saveCycle = async (e) => {
    e.preventDefault();
    setSavingCy(true);
    try {
      const payload = { ...cyForm, hourlyRate: selected.hourlyRate };
      if (editCy) {
        await api.put(`/upwork/${selected.id}/cycles/${editCy.id}`, payload);
        toast.success('Cycle updated');
      } else {
        await api.post(`/upwork/${selected.id}/cycles`, payload);
        toast.success('Cycle added');
      }
      setCyModal(false);
      loadAll();
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    setSavingCy(false);
  };

  const deleteCy = async (cy) => {
    if (!await confirm({ title:'Delete Cycle', message:'Delete this weekly cycle?', confirmText:'Delete', danger:true })) return;
    try { await api.delete(`/upwork/${selected.id}/cycles/${cy.id}`); toast.success('Deleted'); loadAll(); }
    catch { toast.error('Failed'); }
  };

  const S = {
    page: { padding:'28px 32px', background:'#f8fafc', minHeight:'100vh', fontFamily:"'DM Sans',sans-serif" },
    hdr:  { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 },
    h1:   { margin:0, fontSize:24, fontWeight:800, color:'#1e293b' },
    sub:  { margin:'4px 0 0', fontSize:14, color:'#64748b' },
    btn:  { display:'inline-flex', alignItems:'center', gap:6, padding:'10px 18px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', cursor:'pointer', fontWeight:700, fontSize:14 },
    stat: { background:'#fff', borderRadius:14, padding:'18px 22px', boxShadow:'0 1px 4px #0000000d', display:'flex', alignItems:'center', gap:14 },
    card: { background:'#fff', borderRadius:14, padding:18, boxShadow:'0 1px 4px #0000000d', cursor:'pointer', border:'1.5px solid transparent', transition:'all .2s' },
    tbl:  { width:'100%', borderCollapse:'collapse', fontSize:13 },
    th:   { textAlign:'left', padding:'10px 12px', fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', borderBottom:'1px solid #f1f5f9' },
    td:   { padding:'10px 12px', color:'#334155', borderBottom:'1px solid #f8fafc' },
  };

  // ── List view ──────────────────────────────────────────────────────────────
  if (!selected) return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.hdr}>
        <div>
          <h1 style={S.h1}>Upwork Contracts</h1>
          <p style={S.sub}>Manage fixed & hourly Upwork contracts, milestones and weekly cycles</p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={loadAll} style={{ ...S.btn, background:'#f1f5f9', color:'#64748b' }}><FiRefreshCw size={14}/></button>
          <button onClick={openAddContract} style={S.btn}><FiPlus size={14}/> Add Contract</button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:14, marginBottom:24 }}>
          {[
            { label:'Total Contracts', value: stats.totalContracts,         icon:'📋', color:'var(--pm-primary)', bg:'#eef2ff' },
            { label:'Active',          value: stats.active,                  icon:'✅', color:'#22c55e', bg:'#dcfce7' },
            { label:'Total Billed',    value: fmt(stats.totalBilled),        icon:'💵', color:'#0ea5e9', bg:'#e0f2fe' },
            { label:'Upwork Fees',     value: fmt(stats.totalFees),          icon:'💸', color:'#ef4444', bg:'#fee2e2' },
            { label:'Net Earned',      value: fmt(stats.netEarned),          icon:'💰', color:'#f59e0b', bg:'#fef3c7' },
          ].map(s => (
            <div key={s.label} style={S.stat}>
              <div style={{ width:44, height:44, borderRadius:12, background:s.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>{s.icon}</div>
              <div>
                <div style={{ fontSize:18, fontWeight:800, color:s.color }}>{s.value}</div>
                <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Contracts */}
      {loading ? (
        <div style={{ textAlign:'center', padding:64, color:'#94a3b8' }}>Loading…</div>
      ) : contracts.length === 0 ? (
        <div style={{ textAlign:'center', padding:80, background:'#fff', borderRadius:16, boxShadow:'0 1px 4px #0000000d' }}>
          <div style={{ fontSize:48, marginBottom:12 }}>💼</div>
          <div style={{ fontWeight:700, fontSize:16, color:'#1e293b', marginBottom:6 }}>No contracts yet</div>
          <div style={{ fontSize:13, color:'#94a3b8', marginBottom:20 }}>Add your first Upwork contract to start tracking</div>
          <button onClick={openAddContract} style={S.btn}><FiPlus size={13}/> Add Contract</button>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))', gap:16 }}>
          {contracts.map(c => {
            const pColor = PLATFORM_COLORS[c.platform] || PLATFORM_COLORS.upwork_company;
            const sColor = STATUS_COLORS[c.status]     || STATUS_COLORS.active;
            const ms = c.milestones || [];
            const cy = c.cycles || [];
            const totalMs = ms.reduce((s,m)=>s+parseFloat(m.amount||0),0);
            const releasedMs = ms.filter(m=>m.status==='released').reduce((s,m)=>s+parseFloat(m.amount||0),0);
            const totalCyNet = cy.reduce((s,x)=>s+parseFloat(x.netAmount||0),0);
            return (
              <div key={c.id} style={S.card}
                onClick={()=>setSelected(c)}
                onMouseEnter={ev=>{ev.currentTarget.style.transform='translateY(-2px)';ev.currentTarget.style.boxShadow='0 8px 24px #0000001a';ev.currentTarget.style.borderColor='var(--pm-primary)';}}
                onMouseLeave={ev=>{ev.currentTarget.style.transform='';ev.currentTarget.style.boxShadow='0 1px 4px #0000000d';ev.currentTarget.style.borderColor='transparent';}}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, color:'#1e293b', fontSize:15, marginBottom:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.title}</div>
                    <div style={{ fontSize:12, color:'#64748b' }}>👤 {c.clientName}</div>
                  </div>
                  <div style={{ display:'flex', gap:6, alignItems:'center', marginLeft:8 }}>
                    <button onClick={(e)=>openEditContract(c,e)} style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', padding:4 }}><FiEdit2 size={13}/></button>
                    <button onClick={(e)=>deleteContract(c,e)} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:4 }}><FiTrash2 size={13}/></button>
                  </div>
                </div>

                <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12 }}>
                  {badge(c.type === 'hourly' ? '⏱ Hourly' : '📌 Fixed', { background:'#f0f0ff', color:'var(--pm-primary)' })}
                  {badge(PLATFORM_LABELS[c.platform], { background: pColor.bg, color: pColor.text })}
                  {badge(c.status, { background: sColor.bg, color: sColor.text })}
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                  {c.type === 'hourly' ? (
                    <>
                      <div style={{ background:'#f8fafc', borderRadius:8, padding:'8px 12px' }}>
                        <div style={{ fontSize:11, color:'#94a3b8' }}>Rate</div>
                        <div style={{ fontWeight:700, color:'#1e293b', fontSize:15 }}>${c.hourlyRate}/hr</div>
                      </div>
                      <div style={{ background:'#f8fafc', borderRadius:8, padding:'8px 12px' }}>
                        <div style={{ fontSize:11, color:'#94a3b8' }}>Net Earned</div>
                        <div style={{ fontWeight:700, color:'#22c55e', fontSize:15 }}>{fmt(totalCyNet)}</div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ background:'#f8fafc', borderRadius:8, padding:'8px 12px' }}>
                        <div style={{ fontSize:11, color:'#94a3b8' }}>Contract Value</div>
                        <div style={{ fontWeight:700, color:'#1e293b', fontSize:15 }}>{fmt(c.totalAmount)}</div>
                      </div>
                      <div style={{ background:'#f8fafc', borderRadius:8, padding:'8px 12px' }}>
                        <div style={{ fontSize:11, color:'#94a3b8' }}>Released</div>
                        <div style={{ fontWeight:700, color:'#22c55e', fontSize:15 }}>{fmt(releasedMs)} / {fmt(totalMs)}</div>
                      </div>
                    </>
                  )}
                </div>

                {c.project && (
                  <div style={{ fontSize:12, color:'var(--pm-primary)', display:'flex', alignItems:'center', gap:5 }}>
                    📁 {c.project.name}
                  </div>
                )}

                <div style={{ display:'flex', justifyContent:'space-between', marginTop:10, paddingTop:10, borderTop:'1px solid #f1f5f9', fontSize:12, color:'#94a3b8' }}>
                  <span>Fee: {c.feePercent}%</span>
                  {c.type==='hourly' ? <span>{cy.length} weekly cycles</span> : <span>{ms.length} milestones</span>}
                  {c.startDate && <span>Since {new Date(c.startDate).toLocaleDateString('en-IN',{month:'short',year:'numeric'})}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Contract Modal */}
      {contractModal && (
        <div style={{ position:'fixed', inset:0, background:'#0007', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div style={{ background:'#fff', borderRadius:20, padding:28, width:'100%', maxWidth:560, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px #0000002a' }}>
            <h3 style={{ margin:'0 0 20px', fontSize:18, fontWeight:700 }}>{editContract ? 'Edit Contract' : 'Add Upwork Contract'}</h3>
            <form onSubmit={saveContract} style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div style={{ gridColumn:'1/-1' }}>
                  <label style={LBL}>Contract Title *</label>
                  <input style={INP} value={contractForm.title} onChange={e=>setContractForm(f=>({...f,title:e.target.value}))} placeholder="e.g. React Dashboard Development" required />
                </div>
                <div>
                  <label style={LBL}>Client Name *</label>
                  <input style={INP} value={contractForm.clientName} onChange={e=>setContractForm(f=>({...f,clientName:e.target.value}))} placeholder="Client's Upwork name" required />
                </div>
                <div>
                  <label style={LBL}>Upwork Contract ID</label>
                  <input style={INP} value={contractForm.contractId} onChange={e=>setContractForm(f=>({...f,contractId:e.target.value}))} placeholder="~01abc123 (optional)" />
                </div>
                <div>
                  <label style={LBL}>Type *</label>
                  <select style={INP} value={contractForm.type} onChange={e=>setContractForm(f=>({...f,type:e.target.value}))} required>
                    <option value="hourly">⏱ Hourly</option>
                    <option value="fixed">📌 Fixed Price</option>
                  </select>
                </div>
                <div>
                  <label style={LBL}>Platform</label>
                  <select style={INP} value={contractForm.platform} onChange={e=>setContractForm(f=>({...f,platform:e.target.value}))}>
                    <option value="upwork_company">Upwork Company Account</option>
                    <option value="upwork_personal">Upwork Personal Account</option>
                  </select>
                </div>

                {contractForm.type === 'hourly' ? (
                  <>
                    <div>
                      <label style={LBL}>Hourly Rate (USD) *</label>
                      <input style={INP} type="number" step="0.01" value={contractForm.hourlyRate} onChange={e=>setContractForm(f=>({...f,hourlyRate:e.target.value}))} placeholder="25.00" required />
                    </div>
                    <div>
                      <label style={LBL}>Weekly Hours Limit</label>
                      <input style={INP} type="number" step="0.5" value={contractForm.weeklyLimit} onChange={e=>setContractForm(f=>({...f,weeklyLimit:e.target.value}))} placeholder="40" />
                    </div>
                  </>
                ) : (
                  <div style={{ gridColumn:'1/-1' }}>
                    <label style={LBL}>Contract Value (USD) *</label>
                    <input style={INP} type="number" step="0.01" value={contractForm.totalAmount} onChange={e=>setContractForm(f=>({...f,totalAmount:e.target.value}))} placeholder="5000.00" required />
                  </div>
                )}

                <div>
                  <label style={LBL}>Upwork Fee %</label>
                  <input style={INP} type="number" step="0.01" value={contractForm.feePercent} onChange={e=>setContractForm(f=>({...f,feePercent:e.target.value}))} placeholder="10" />
                </div>
                <div>
                  <label style={LBL}>Status</label>
                  <select style={INP} value={contractForm.status} onChange={e=>setContractForm(f=>({...f,status:e.target.value}))}>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="ended">Ended</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
                <div>
                  <label style={LBL}>Linked Project</label>
                  <select style={INP} value={contractForm.projectId} onChange={e=>setContractForm(f=>({...f,projectId:e.target.value}))}>
                    <option value="">— None —</option>
                    {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={LBL}>Start Date</label>
                  <input style={INP} type="date" value={contractForm.startDate} onChange={e=>setContractForm(f=>({...f,startDate:e.target.value}))} />
                </div>
                <div>
                  <label style={LBL}>End Date</label>
                  <input style={INP} type="date" value={contractForm.endDate} onChange={e=>setContractForm(f=>({...f,endDate:e.target.value}))} />
                </div>
                <div style={{ gridColumn:'1/-1' }}>
                  <label style={LBL}>Notes</label>
                  <textarea style={{ ...INP, height:70, resize:'vertical' }} value={contractForm.notes} onChange={e=>setContractForm(f=>({...f,notes:e.target.value}))} placeholder="Contract notes…" />
                </div>
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:4 }}>
                <button type="button" onClick={()=>setContractModal(false)} style={{ padding:'10px 20px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#f8fafc', color:'#64748b', cursor:'pointer', fontWeight:600, fontSize:14 }}>Cancel</button>
                <button type="submit" disabled={savingC} style={{ padding:'10px 22px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', cursor:'pointer', fontWeight:700, fontSize:14, opacity:savingC?0.7:1 }}>
                  {savingC ? 'Saving…' : editContract ? 'Update' : 'Add Contract'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  // ── Detail view ────────────────────────────────────────────────────────────
  const c = selected;
  const milestones = c.milestones || [];
  const cycles     = c.cycles     || [];
  const sColor = STATUS_COLORS[c.status] || STATUS_COLORS.active;
  const pColor = PLATFORM_COLORS[c.platform] || PLATFORM_COLORS.upwork_company;
  const totalCyBilled = cycles.reduce((s,x)=>s+parseFloat(x.billedAmount||0),0);
  const totalCyFees   = cycles.reduce((s,x)=>s+parseFloat(x.feeAmount||0),0);
  const totalCyNet    = cycles.reduce((s,x)=>s+parseFloat(x.netAmount||0),0);
  const totalCyHours  = cycles.reduce((s,x)=>s+parseFloat(x.hoursWorked||0),0);
  const totalMs       = milestones.reduce((s,m)=>s+parseFloat(m.amount||0),0);
  const releasedMs    = milestones.filter(m=>m.status==='released').reduce((s,m)=>s+parseFloat(m.amount||0),0);

  return (
    <div style={S.page}>
      {/* Top bar */}
      <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:24 }}>
        <button onClick={()=>setSelected(null)} style={{ padding:'8px 14px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontSize:14, color:'#475569' }}>
          <FiArrowLeft size={14}/> All Contracts
        </button>
        <div style={{ flex:1 }}>
          <h1 style={{ margin:0, fontSize:20, fontWeight:800, color:'#1e293b' }}>{c.title}</h1>
          <p style={{ margin:'2px 0 0', color:'#64748b', fontSize:13 }}>👤 {c.clientName}</p>
        </div>
        <button onClick={()=>openEditContract(c,{stopPropagation:()=>{}})} style={{ ...S.btn, background:'#f1f5f9', color:'#475569' }}><FiEdit2 size={13}/> Edit</button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'260px 1fr', gap:20, alignItems:'start' }}>
        {/* Sidebar */}
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ background:'#fff', borderRadius:14, padding:20, boxShadow:'0 1px 4px #0000000d' }}>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:16 }}>
              {badge(c.type === 'hourly' ? '⏱ Hourly' : '📌 Fixed', { background:'#f0f0ff', color:'var(--pm-primary)' })}
              {badge(PLATFORM_LABELS[c.platform], { background: pColor.bg, color: pColor.text })}
              {badge(c.status, { background: sColor.bg, color: sColor.text })}
            </div>
            {[
              { label:'Fee %',    value: `${c.feePercent}%` },
              c.type==='hourly' ? { label:'Rate',      value: `$${c.hourlyRate}/hr` } : null,
              c.type==='hourly' ? { label:'Weekly Limit', value: c.weeklyLimit ? `${c.weeklyLimit} hrs` : '—' } : null,
              c.type==='fixed'  ? { label:'Contract Value', value: fmt(c.totalAmount) } : null,
              { label:'Currency', value: c.currency || 'USD' },
              c.startDate ? { label:'Start', value: new Date(c.startDate).toLocaleDateString('en-IN') } : null,
              c.endDate   ? { label:'End',   value: new Date(c.endDate).toLocaleDateString('en-IN') }   : null,
            ].filter(Boolean).map((row,i)=>(
              <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid #f1f5f9', fontSize:13 }}>
                <span style={{ color:'#64748b' }}>{row.label}</span>
                <span style={{ fontWeight:700, color:'#1e293b' }}>{row.value}</span>
              </div>
            ))}
            {c.project && (
              <div style={{ marginTop:12, padding:'8px 12px', background:'#f0f0ff', borderRadius:8, fontSize:12, color:'var(--pm-primary)', fontWeight:600 }}>📁 {c.project.name}</div>
            )}
          </div>

          {/* Summary box */}
          <div style={{ background:'#fff', borderRadius:14, padding:20, boxShadow:'0 1px 4px #0000000d' }}>
            <div style={{ fontWeight:700, fontSize:14, marginBottom:12, color:'#1e293b' }}>Summary</div>
            {c.type === 'hourly' ? [
              { label:'Total Hours',  value: totalCyHours.toFixed(1) + ' hrs' },
              { label:'Gross Billed', value: fmt(totalCyBilled) },
              { label:'Upwork Fees',  value: fmt(totalCyFees) },
              { label:'Net Earned',   value: fmt(totalCyNet), bold:true, color:'#22c55e' },
            ] : [
              { label:'Contract Value', value: fmt(c.totalAmount) },
              { label:'Total Milestones', value: fmt(totalMs) },
              { label:'Released',       value: fmt(releasedMs), bold:true, color:'#22c55e' },
              { label:'Pending',        value: fmt(totalMs - releasedMs) },
            ].map((row,i)=>(
              <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid #f1f5f9', fontSize:13 }}>
                <span style={{ color:'#64748b' }}>{row.label}</span>
                <span style={{ fontWeight: row.bold?800:700, color: row.color||'#1e293b', fontSize: row.bold?15:13 }}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Main content */}
        <div>
          {c.type === 'fixed' ? (
            <div style={{ background:'#fff', borderRadius:16, padding:22, boxShadow:'0 1px 4px #0000000d' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:'#1e293b' }}>📌 Milestones</h3>
                <button onClick={openAddMs} style={{ ...S.btn, padding:'7px 14px', fontSize:13 }}><FiPlus size={12}/> Add</button>
              </div>
              {milestones.length === 0 ? (
                <div style={{ textAlign:'center', padding:40, color:'#94a3b8' }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>📌</div>
                  <div>No milestones yet. Add your first milestone.</div>
                </div>
              ) : (
                <table style={S.tbl}>
                  <thead><tr>
                    <th style={S.th}>Milestone</th>
                    <th style={S.th}>Amount</th>
                    <th style={S.th}>Status</th>
                    <th style={S.th}>Due</th>
                    <th style={S.th}>Actions</th>
                  </tr></thead>
                  <tbody>
                    {milestones.map(m => {
                      const mc = MS_COLORS[m.status] || MS_COLORS.pending;
                      return (
                        <tr key={m.id}>
                          <td style={S.td}><div style={{ fontWeight:600 }}>{m.name}</div>{m.description&&<div style={{ fontSize:11, color:'#94a3b8' }}>{m.description}</div>}</td>
                          <td style={{ ...S.td, fontWeight:700, color:'#22c55e' }}>{fmt(m.amount)}</td>
                          <td style={S.td}>{badge(m.status.replace('_',' '), { background:mc.bg, color:mc.text })}</td>
                          <td style={{ ...S.td, color:'#64748b' }}>{m.dueDate ? new Date(m.dueDate).toLocaleDateString('en-IN') : '—'}</td>
                          <td style={S.td}>
                            <div style={{ display:'flex', gap:6 }}>
                              <button onClick={()=>openEditMs(m)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--pm-primary)', padding:4 }}><FiEdit2 size={13}/></button>
                              <button onClick={()=>deleteMs(m)} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:4 }}><FiTrash2 size={13}/></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <div style={{ background:'#fff', borderRadius:16, padding:22, boxShadow:'0 1px 4px #0000000d' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:'#1e293b' }}>⏱ Weekly Billing Cycles</h3>
                <button onClick={openAddCy} style={{ ...S.btn, padding:'7px 14px', fontSize:13 }}><FiPlus size={12}/> Add Week</button>
              </div>
              {cycles.length === 0 ? (
                <div style={{ textAlign:'center', padding:40, color:'#94a3b8' }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>⏱</div>
                  <div>No billing cycles yet. Add your first weekly cycle.</div>
                </div>
              ) : (
                <table style={S.tbl}>
                  <thead><tr>
                    <th style={S.th}>Week</th>
                    <th style={S.th}>Hours</th>
                    <th style={S.th}>Rate</th>
                    <th style={S.th}>Billed</th>
                    <th style={S.th}>Fee ({cycles[0]?.feePercent||10}%)</th>
                    <th style={S.th}>Net</th>
                    <th style={S.th}>Status</th>
                    <th style={S.th}>Actions</th>
                  </tr></thead>
                  <tbody>
                    {cycles.map(cy => {
                      const cc = CY_COLORS[cy.status] || CY_COLORS.open;
                      return (
                        <tr key={cy.id}>
                          <td style={{ ...S.td, whiteSpace:'nowrap', fontSize:12 }}>
                            {new Date(cy.weekStart).toLocaleDateString('en-IN',{month:'short',day:'numeric'})} – {new Date(cy.weekEnd).toLocaleDateString('en-IN',{month:'short',day:'numeric',year:'numeric'})}
                          </td>
                          <td style={{ ...S.td, fontWeight:700 }}>{parseFloat(cy.hoursWorked).toFixed(1)}</td>
                          <td style={{ ...S.td, color:'#64748b' }}>${cy.hourlyRate}/hr</td>
                          <td style={{ ...S.td, fontWeight:700 }}>{fmt(cy.billedAmount)}</td>
                          <td style={{ ...S.td, color:'#ef4444' }}>-{fmt(cy.feeAmount)}</td>
                          <td style={{ ...S.td, fontWeight:800, color:'#22c55e' }}>{fmt(cy.netAmount)}</td>
                          <td style={S.td}>{badge(cy.status.replace('_',' '), { background:cc.bg, color:cc.text })}</td>
                          <td style={S.td}>
                            <div style={{ display:'flex', gap:6 }}>
                              <button onClick={()=>openEditCy(cy)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--pm-primary)', padding:4 }}><FiEdit2 size={13}/></button>
                              <button onClick={()=>deleteCy(cy)} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:4 }}><FiTrash2 size={13}/></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background:'#f8fafc' }}>
                      <td style={{ ...S.td, fontWeight:700, color:'#475569' }}>Total</td>
                      <td style={{ ...S.td, fontWeight:800 }}>{totalCyHours.toFixed(1)}</td>
                      <td style={S.td}></td>
                      <td style={{ ...S.td, fontWeight:800 }}>{fmt(totalCyBilled)}</td>
                      <td style={{ ...S.td, fontWeight:800, color:'#ef4444' }}>-{fmt(totalCyFees)}</td>
                      <td style={{ ...S.td, fontWeight:800, color:'#22c55e', fontSize:15 }}>{fmt(totalCyNet)}</td>
                      <td style={S.td} colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          )}
          {c.notes && (
            <div style={{ marginTop:16, background:'#fff', borderRadius:14, padding:16, boxShadow:'0 1px 4px #0000000d', fontSize:13, color:'#475569' }}>
              <strong>Notes:</strong> {c.notes}
            </div>
          )}
        </div>
      </div>

      {/* Milestone Modal */}
      {msModal && (
        <div style={{ position:'fixed', inset:0, background:'#0007', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div style={{ background:'#fff', borderRadius:18, padding:28, width:'100%', maxWidth:420, boxShadow:'0 20px 60px #0000002a' }}>
            <h3 style={{ margin:'0 0 20px', fontSize:17, fontWeight:700 }}>{editMs ? 'Edit Milestone' : 'Add Milestone'}</h3>
            <form onSubmit={saveMilestone} style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div><label style={LBL}>Milestone Name *</label><input style={INP} value={msForm.name} onChange={e=>setMsForm(f=>({...f,name:e.target.value}))} required placeholder="e.g. Phase 1 - Design" /></div>
              <div><label style={LBL}>Description</label><textarea style={{ ...INP, height:60, resize:'vertical' }} value={msForm.description} onChange={e=>setMsForm(f=>({...f,description:e.target.value}))} /></div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div><label style={LBL}>Amount (USD) *</label><input style={INP} type="number" step="0.01" value={msForm.amount} onChange={e=>setMsForm(f=>({...f,amount:e.target.value}))} required /></div>
                <div><label style={LBL}>Due Date</label><input style={INP} type="date" value={msForm.dueDate} onChange={e=>setMsForm(f=>({...f,dueDate:e.target.value}))} /></div>
              </div>
              <div><label style={LBL}>Status</label>
                <select style={INP} value={msForm.status} onChange={e=>setMsForm(f=>({...f,status:e.target.value}))}>
                  {['pending','funded','in_progress','submitted','approved','released'].map(s=><option key={s} value={s}>{s.replace('_',' ')}</option>)}
                </select>
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:4 }}>
                <button type="button" onClick={()=>setMsModal(false)} style={{ padding:'9px 18px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#f8fafc', color:'#64748b', cursor:'pointer', fontWeight:600, fontSize:13 }}>Cancel</button>
                <button type="submit" disabled={savingMs} style={{ padding:'9px 18px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', cursor:'pointer', fontWeight:700, fontSize:13, opacity:savingMs?0.7:1 }}>{savingMs?'Saving…':editMs?'Update':'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cycle Modal */}
      {cyModal && (
        <div style={{ position:'fixed', inset:0, background:'#0007', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div style={{ background:'#fff', borderRadius:18, padding:28, width:'100%', maxWidth:400, boxShadow:'0 20px 60px #0000002a' }}>
            <h3 style={{ margin:'0 0 20px', fontSize:17, fontWeight:700 }}>{editCy ? 'Edit Cycle' : 'Add Weekly Cycle'}</h3>
            <form onSubmit={saveCycle} style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div style={{ background:'#f0f0ff', borderRadius:8, padding:'10px 14px', fontSize:12, color:'var(--pm-primary)', marginBottom:4 }}>
                Rate: <strong>${c.hourlyRate}/hr</strong> — Upwork Fee: <strong>{c.feePercent}%</strong>
                {cyForm.hoursWorked && <span> → Net: <strong>${((parseFloat(cyForm.hoursWorked||0) * parseFloat(c.hourlyRate||0)) * (1 - parseFloat(c.feePercent||0)/100)).toFixed(2)}</strong></span>}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div><label style={LBL}>Week Start *</label><input style={INP} type="date" value={cyForm.weekStart} onChange={e=>setCyForm(f=>({...f,weekStart:e.target.value}))} required /></div>
                <div><label style={LBL}>Week End *</label><input style={INP} type="date" value={cyForm.weekEnd} onChange={e=>setCyForm(f=>({...f,weekEnd:e.target.value}))} required /></div>
              </div>
              <div><label style={LBL}>Hours Worked *</label><input style={INP} type="number" step="0.01" value={cyForm.hoursWorked} onChange={e=>setCyForm(f=>({...f,hoursWorked:e.target.value}))} required placeholder="40.0" /></div>
              {editCy && (
                <div><label style={LBL}>Status</label>
                  <select style={INP} value={cyForm.status||'open'} onChange={e=>setCyForm(f=>({...f,status:e.target.value}))}>
                    {['open','pending_review','billed','paid'].map(s=><option key={s} value={s}>{s.replace('_',' ')}</option>)}
                  </select>
                </div>
              )}
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:4 }}>
                <button type="button" onClick={()=>setCyModal(false)} style={{ padding:'9px 18px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#f8fafc', color:'#64748b', cursor:'pointer', fontWeight:600, fontSize:13 }}>Cancel</button>
                <button type="submit" disabled={savingCy} style={{ padding:'9px 18px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', cursor:'pointer', fontWeight:700, fontSize:13, opacity:savingCy?0.7:1 }}>{savingCy?'Saving…':editCy?'Update':'Add Week'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Contract edit modal (reused) */}
      {contractModal && (
        <div style={{ position:'fixed', inset:0, background:'#0007', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div style={{ background:'#fff', borderRadius:20, padding:28, width:'100%', maxWidth:560, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px #0000002a' }}>
            <h3 style={{ margin:'0 0 20px', fontSize:18, fontWeight:700 }}>Edit Contract</h3>
            <form onSubmit={saveContract} style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div style={{ gridColumn:'1/-1' }}><label style={LBL}>Title *</label><input style={INP} value={contractForm.title} onChange={e=>setContractForm(f=>({...f,title:e.target.value}))} required /></div>
                <div><label style={LBL}>Client Name *</label><input style={INP} value={contractForm.clientName} onChange={e=>setContractForm(f=>({...f,clientName:e.target.value}))} required /></div>
                <div><label style={LBL}>Status</label>
                  <select style={INP} value={contractForm.status} onChange={e=>setContractForm(f=>({...f,status:e.target.value}))}>
                    {['active','paused','ended','suspended'].map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                {contractForm.type==='hourly' ? (
                  <>
                    <div><label style={LBL}>Hourly Rate</label><input style={INP} type="number" step="0.01" value={contractForm.hourlyRate} onChange={e=>setContractForm(f=>({...f,hourlyRate:e.target.value}))} /></div>
                    <div><label style={LBL}>Weekly Limit</label><input style={INP} type="number" step="0.5" value={contractForm.weeklyLimit} onChange={e=>setContractForm(f=>({...f,weeklyLimit:e.target.value}))} /></div>
                  </>
                ) : (
                  <div><label style={LBL}>Contract Value</label><input style={INP} type="number" step="0.01" value={contractForm.totalAmount} onChange={e=>setContractForm(f=>({...f,totalAmount:e.target.value}))} /></div>
                )}
                <div><label style={LBL}>Fee %</label><input style={INP} type="number" step="0.01" value={contractForm.feePercent} onChange={e=>setContractForm(f=>({...f,feePercent:e.target.value}))} /></div>
                <div><label style={LBL}>Linked Project</label>
                  <select style={INP} value={contractForm.projectId} onChange={e=>setContractForm(f=>({...f,projectId:e.target.value}))}>
                    <option value="">— None —</option>
                    {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn:'1/-1' }}><label style={LBL}>Notes</label><textarea style={{ ...INP, height:60, resize:'vertical' }} value={contractForm.notes} onChange={e=>setContractForm(f=>({...f,notes:e.target.value}))} /></div>
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                <button type="button" onClick={()=>setContractModal(false)} style={{ padding:'10px 20px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#f8fafc', color:'#64748b', cursor:'pointer', fontWeight:600, fontSize:14 }}>Cancel</button>
                <button type="submit" disabled={savingC} style={{ padding:'10px 22px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', cursor:'pointer', fontWeight:700, fontSize:14, opacity:savingC?0.7:1 }}>{savingC?'Saving…':'Update'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
